/**
 * Race a promise against a timeout, rejecting if it does not settle in time.
 *
 * WHY (ENG-4034): the auth→studio-shell gate chain (`ProfileSynchronizer`,
 * `AutoLinkGuard`) `await`s network calls — `getAccessTokenSilently()` and the
 * mailroom `UserRegistryGetUser` (`fetchStudioUser`) — that have no client-side
 * deadline. On a first boot after an update, with a slow or partitioned network
 * and no cached studio user, one of those requests can HANG (never resolve, never
 * reject). The gate then renders its `<Loader/>` forever above `<Root>`, where the
 * 17s `TimeoutWatchdog` has not mounted yet — producing the white-screen-of-death
 * reproduced by hanging exactly those requests over CDP. Wrapping the await in a
 * timeout converts the hang into a rejection, which the gate's existing catch
 * handles (Auth0-only fallback / error boundary) instead of blanking.
 *
 * The codebase already does this ad-hoc in several places (electron/api.ts:218,
 * newts/router.ts, livekit/livekit.ts, auto-link-prewarm.ts) via inline
 * `Promise.race` + `setTimeout`. Per `think-in-systems` this is the single shared
 * primitive; unlike the inline copies it clears its own timer so a fast-resolving
 * promise does not leave a dangling timeout.
 */
export class TimeoutError extends Error {
  constructor(label: string, ms: number) {
    super(`${label} timed out after ${ms}ms`);
    this.name = 'TimeoutError';
  }
}

export const withTimeout = <T>(
  promise: Promise<T>,
  ms: number,
  label = 'operation'
): Promise<T> => {
  let timer: ReturnType<typeof setTimeout>;

  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new TimeoutError(label, ms)), ms);
  });

  // clearTimeout on settle (either branch) so a resolved promise does not keep
  // the timer — and, in tests with fake timers, does not leak across cases.
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};
